import PropTypes from 'prop-types';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Info } from "lucide-react";
import { PatientCombobox } from "@/components/PatientCombobox";
import { formatRupees, toPaise } from "@/utils/currency";

/**
 * @typedef {import('../types/schema').Appointment} Appointment
 * @typedef {import('../types/schema').Patient} Patient
 * @typedef {import('../types/schema').Clinic} Clinic
 */

export function AppointmentFormDialog({
    onOpenChange,
    formData,
    setFormData,
    onSubmit,
    activePatients,
    clinics,
    clinicDoctors,
    isReceptionistOnly,
    needsDoctorSelector,
    handlePatientFromServer,
    setGlobalPatient,
    token,
    dbSettings,
    // Facility charge props
    isDoctor,
    myRoleForSelectedClinic,
    bestRule,
    selectedPatient,
    calculatedCharge,
    // Add new patient
    onAddNewPatient
}) {
    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        onSubmit(e);
    };

    return (
        <DialogContent
            data-testid="add-appointment-dialog"
            style={{
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <DialogHeader>
                <DialogTitle>Schedule Appointment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} style={{ overflow: 'auto', flex: 1, paddingBottom: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <Label htmlFor="patient">Patient</Label>
                        <PatientCombobox
                            patients={activePatients}
                            value={formData.patient_id}
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    patient_id: value,
                                    skip_facility_charge: false,
                                    override_charge_amount: "",
                                    fee_agreement_id: ""
                                });
                                if (setGlobalPatient) {
                                    setGlobalPatient(null);
                                }
                            }}
                            placeholder="Select patient"
                            token={token}
                            enableGlobalSearch={true}
                            onPatientFromServer={handlePatientFromServer}
                            onAddNewPatient={onAddNewPatient}
                        />
                    </div>
                    {clinics.length > 0 && (
                        <div>
                            <Label htmlFor="clinic">Clinic</Label>
                            <Select
                                value={formData.clinic_id}
                                onValueChange={(value) => setFormData({
                                    ...formData,
                                    clinic_id: value === 'personal' ? '' : value,
                                    selected_doctor_id: ''
                                })}
                            >
                                <SelectTrigger id="clinic" data-testid="clinic-select">
                                    <SelectValue placeholder="Personal (No Clinic)" />
                                </SelectTrigger>
                                <SelectContent>
                                    {!isReceptionistOnly && <SelectItem value="personal">Personal (No Clinic)</SelectItem>}
                                    {clinics.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Doctor selector for receptionists/admins scheduling for other doctors */}
                    {needsDoctorSelector && formData.clinic_id && clinicDoctors.length > 0 && (
                        <div>
                            <Label htmlFor="doctor">Doctor *</Label>
                            <Select
                                value={formData.selected_doctor_id}
                                onValueChange={(value) => setFormData({ ...formData, selected_doctor_id: value })}
                            >
                                <SelectTrigger id="doctor" data-testid="doctor-select">
                                    <SelectValue placeholder="Select doctor..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {clinicDoctors.map(d => (
                                        <SelectItem key={d.user_id} value={d.user_id}>
                                            {d.doctor_name || 'Unknown'} ({d.role})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label htmlFor="date_time">Date & Time</Label>
                        <Input
                            id="date_time"
                            data-testid="datetime-input"
                            type="datetime-local"
                            value={formData.date_time}
                            onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="duration">Duration (minutes)</Label>
                        <div className="flex gap-2 items-center mt-1">
                            <Input
                                id="duration"
                                data-testid="duration-input"
                                type="number"
                                step="1"
                                min="1"
                                max="480"
                                placeholder="Minutes"
                                value={formData.duration_minutes || dbSettings?.default_appointment_duration || 30}
                                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || '' })}
                                onKeyDown={(e) => {
                                    if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                        e.preventDefault();
                                    }
                                }}
                                className="w-24"
                            />
                            <div className="flex gap-1">
                                {[15, 30, 45, 60].map((mins) => (
                                    <Button
                                        key={mins}
                                        type="button"
                                        variant={formData.duration_minutes === mins ? "default" : "outline"}
                                        size="sm"
                                        className="h-9 px-2 text-xs"
                                        onClick={() => setFormData({ ...formData, duration_minutes: mins })}
                                    >
                                        {mins === 60 ? '1h' : `${mins}m`}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="fee_amount">Fee Amount (₹) - Enter whole rupees</Label>
                        <Input
                            id="fee_amount"
                            data-testid="fee-input"
                            type="number"
                            step="1"
                            min="0"
                            placeholder="Amount in Rupees (no decimals)"
                            value={formData.fee_amount}
                            onChange={(e) => setFormData({ ...formData, fee_amount: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                    e.preventDefault();
                                }
                            }}
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="notes">Notes</Label>
                        <Input
                            id="notes"
                            data-testid="notes-input"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        />
                    </div>

                    {/* Facility Charge Preview Section */}
                    {isDoctor && myRoleForSelectedClinic !== 'receptionist' && (bestRule || selectedPatient?.referral_doctor_id) && (
                        <div
                            style={{
                                marginTop: '0.5rem',
                                padding: '1rem',
                                background: '#f0fdf4',
                                borderRadius: '8px',
                                border: '1px solid #86efac'
                            }}
                            data-testid="facility_charge-preview"
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                <Info size={16} color="#16a34a" />
                                <span style={{ fontWeight: '600', color: '#166534' }}>Facility Charge Info</span>
                            </div>

                            <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem' }}>
                                <div><strong>Clinic:</strong> {clinics?.find(c => c.id === formData.clinic_id)?.name || 'Personal (No Clinic)'}</div>
                                {bestRule ? (
                                    <>
                                        <div><strong>Agreement:</strong> {bestRule.name}</div>
                                        <div>
                                            <strong>Charge:</strong> {bestRule.rule_type === 'percent'
                                                ? `${bestRule.fee_value}%`
                                                : `${formatRupees(bestRule.fee_value)} (flat)`
                                            }
                                        </div>
                                    </>
                                ) : (
                                    <div>
                                        <strong>Charge:</strong> {selectedPatient.rule_type === 'percent'
                                            ? `${selectedPatient.fee_value}%`
                                            : `${formatRupees(selectedPatient.fee_value)} (flat)`
                                        }
                                    </div>
                                )}
                                {calculatedCharge !== null && formData.fee_amount && !formData.skip_facility_charge && (
                                    <div style={{ marginTop: '0.5rem', fontSize: '1rem', fontWeight: '600', color: '#16a34a' }}>
                                        Calculated Amount: {formData.override_charge_amount
                                            ? formatRupees(toPaise(formData.override_charge_amount))
                                            : formatRupees(calculatedCharge)
                                        }
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="skip_facility_charge"
                                        checked={formData.skip_facility_charge}
                                        onCheckedChange={(checked) => setFormData({
                                            ...formData,
                                            skip_facility_charge: checked,
                                            override_charge_amount: checked ? "" : formData.override_charge_amount
                                        })}
                                    />
                                    <Label htmlFor="skip_facility_charge" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                                        Skip facility charge for this appointment
                                    </Label>
                                </div>

                                {!formData.skip_facility_charge && (
                                    <div>
                                        <Label htmlFor="override_amount" style={{ fontSize: '0.875rem' }}>
                                            Override Charge Amount (optional)
                                        </Label>
                                        <Input
                                            id="override_amount"
                                            data-testid="override-facility_charge-input"
                                            type="number"
                                            step="1"
                                            min="0"
                                            placeholder={calculatedCharge ? `Default: ${formatRupees(calculatedCharge)}` : "Amount in Rupees"}
                                            value={formData.override_charge_amount}
                                            onChange={(e) => setFormData({ ...formData, override_charge_amount: e.target.value })}
                                            onKeyDown={(e) => {
                                                if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            style={{ marginTop: '0.25rem' }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </form>

            <div style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e2e8f0',
                justifyContent: 'flex-end',
                flexShrink: 0,
                background: 'white'
            }}>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>
                <Button
                    data-testid="submit-appointment-button"
                    type="submit"
                    onClick={handleSubmit}
                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
                >
                    Schedule
                </Button>
            </div>
        </DialogContent>
    );
}

AppointmentFormDialog.propTypes = {
    onOpenChange: PropTypes.func,
    formData: PropTypes.object,
    setFormData: PropTypes.func,
    onSubmit: PropTypes.func,
    activePatients: PropTypes.array,
    clinics: PropTypes.array,
    clinicDoctors: PropTypes.array,
    isReceptionistOnly: PropTypes.bool,
    needsDoctorSelector: PropTypes.bool,
    handlePatientFromServer: PropTypes.func,
    setGlobalPatient: PropTypes.func,
    token: PropTypes.string,
    dbSettings: PropTypes.object,
    isDoctor: PropTypes.bool,
    myRoleForSelectedClinic: PropTypes.string,
    bestRule: PropTypes.object,
    selectedPatient: PropTypes.object,
    calculatedCharge: PropTypes.number,
    onAddNewPatient: PropTypes.func
};
